import React, { useMemo, useState, useEffect } from "react";
import { useTable, usePagination } from "react-table";
import Pagination from "@mui/material/Pagination";
import moment from "moment";

const ReactTable = ({
  columns,
  data,
  params,
  setParams,
  serverSide = false,
  totalCount,
}) => {
  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data, [data]);

  const [expandedRows, setExpandedRows] = useState([]);

  // For server-side, pageCount is calculated from totalCount
  const pageCount = serverSide
    ? Math.ceil((totalCount || 0) / params?.page_size)
    : undefined;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // page for client, data for server
    gotoPage,
    state: { pageIndex },
    setPageSize,
    pageOptions,
  } = useTable(
    {
      columns: memoColumns,
      data: memoData,
      initialState: { pageIndex: 0, pageSize: params?.page_size },
      manualPagination: serverSide,
      pageCount: serverSide ? pageCount : undefined,
    },
    usePagination
  );

  // For MUI Pagination (1-based)
  const totalPages = serverSide ? pageCount : pageOptions.length;

  const [gotoPageInput, setGotoPageInput] = useState("");

  // Server-side: call onPageChange when pageIndex or pageSize changes
  useEffect(() => {
    if (serverSide && typeof setParams === "function") {
      setParams({
        ...params,
        page: pageIndex + 1,
        page_size: params?.page_size,
      });
    }
  }, [serverSide, pageIndex, setParams]);

  const handleChange = (event, value) => {
    gotoPage(value - 1);
  };

  const handleGotoPage = () => {
    const pageNum = Number(gotoPageInput);
    if (pageNum > 0 && pageNum <= totalPages) {
      gotoPage(pageNum - 1);
      setGotoPageInput("");
    }
  };

  const handleRowClick = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  return (
    <div className="table_wrapper" style={{ minHeight: "60vh" }}>
      <div style={{ marginTop: "20px" }} className="table_scroll">
        <table {...getTableProps()} className="leaderboard-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} key={column.id}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(serverSide ? memoData : page).map((row, i) => {
              if (!serverSide) prepareRow(row);
              const rowId = serverSide
                ? row.login_id || row.id || i
                : row.original?.login_id || row.id || i;
              const isExpanded = expandedRows.includes(rowId);
              return (
                <>
                  <tr
                    onClick={() => handleRowClick(rowId)}
                    {...(serverSide ? {} : row.getRowProps())}
                    key={serverSide ? row.id || i : row.id}
                  >
                    {(serverSide ? columns : row.cells).map((cell, j) => {
                      let value;
                      if (serverSide) {
                        // Use custom Cell renderer if it exists
                        value =
                          typeof cell.Cell === "function"
                            ? React.createElement(cell.Cell, {
                                value: row[cell.accessor],
                                row: { original: row },
                              })
                            : row[cell.accessor];
                      } else {
                        value = cell.render("Cell");
                      }
                      return (
                        <td
                          className={
                            (serverSide ? cell.Header : cell.column.Header) ===
                            "Status"
                              ? "status_column"
                              : ""
                          }
                          {...(serverSide ? {} : cell.getCellProps())}
                          key={serverSide ? cell.accessor || j : cell.column.id}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>

                  {isExpanded && (
                    <tr className="expanded-row">
                      <td
                        colSpan={serverSide ? columns.length : row.cells.length}
                      >
                        {row.user_audit_logs?.length > 0 ||
                        row.original?.user_audit_logs?.length > 0 ? (
                          <div className="expanded-content">
                            <h4>User Audit Logs</h4>

                            <table className="audit-table">
                              <thead>
                                <tr className="logs_header_tr">
                                  <th>Field</th>
                                  <th>Old Value</th>
                                  <th>New Value</th>
                                  <th>Changed By</th>
                                  <th>IP Address</th>
                                  <th>Reason</th>
                                  <th>Changed At</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(
                                  row.user_audit_logs ||
                                  row.original?.user_audit_logs
                                )?.map((log, idx) => (
                                  <tr key={idx} className="logs_tr">
                                    <td>
                                      {log.field_name?.replace("_", " ") || "-"}
                                    </td>
                                    <td>
                                      {log.old_value?.replace("_", " ") || "-"}
                                    </td>
                                    <td>{log.new_value || "-"}</td>
                                    <td>{log.changed_by || "-"}</td>
                                    <td>{log.ip_address || "-"}</td>
                                    <td className="reason-column">
                                      {log.change_reason || "-"}
                                    </td>
                                    <td>
                                      {moment(log.changed_at).format(
                                        "YYYY-MM-DD HH:mm:ss"
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p>No logs found.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="spacerT30p"></div>
      <div
        className="pagination_wrapper"
        style={{ display: "flex", alignItems: "center", gap: "20px" }}
      >
        <Pagination
          onChange={handleChange}
          count={totalPages}
          variant="outlined"
          page={params?.page}
          color="primary"
          disabled={totalPages === 0}
        />
        <div className="goto-page-input">
          <input
            type="number"
            placeholder="Go to Page"
            value={gotoPageInput}
            min={1}
            max={totalPages}
            onChange={(e) => setGotoPageInput(e.target.value)}
          />
          <button className="goToButton" onClick={handleGotoPage}>
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReactTable;
